import { Component, OnInit, signal, computed, inject, ViewChild, ElementRef, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed, rxResource } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ComplainService } from 'src/app/services/complain/complain-service';
import { ActivatedRoute, Router } from '@angular/router';
import { StoreData } from 'src/app/utils/store_data';
import { ComplainFormStatus } from 'src/app/enumeration/complain-form-status.enum';
import { timer } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { CheckAccessRoles } from 'src/app/utils/check-access-roles';
import { UserLoginRole } from 'src/app/enumeration/roles';
import { ConvertMmtPipe } from 'src/app/utils/convert-mmt.pipe';

@Component({
  selector: 'app-complain-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ConvertMmtPipe
  ],
  templateUrl: './complain-detail.html',
  styleUrls: ['./complain-detail.css']
})
export class ComplainDetail implements OnInit {

  private complainService = inject(ComplainService);
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dataStore = inject(StoreData);
  private checkAccessRoles = inject(CheckAccessRoles);

  @ViewChild('chatScrollContainer') chatScrollContainer!: ElementRef;

  // State Management Signals
  currentComplainId = signal<number>(0);
  currentUser = signal<string>(this.dataStore.getLoginName() || 'devadmin');
  chatInput = signal<string>('');
  complainStatuses = Object.values(ComplainFormStatus);
  protected readonly ComplainFormStatus = ComplainFormStatus;

  // Active Reply Target Signal Anchor
  replyingToMessage = signal<any | null>(null);
  isClosingTicketLoading = signal<boolean>(false);
  isClosingTicketBtnAccess = signal<boolean>(false);

  // Modern Data Pipeline: Updated property keys for Angular Resource APIs
  complainResource = rxResource({
    params: () => ({ id: this.currentComplainId() }),

    stream: ({ params }) => {
      return timer(0, 4000).pipe(
        switchMap(() => this.complainService.getComplainDetail(params.id))
      );
    }
  });

  // Computed Read-Only Signals extracted safely from your modern resource streams
  complainDetail = computed(() => this.complainResource.value() ?? null);
  isLoading = computed(() => this.complainResource.isLoading());

  constructor() {
    // Reactive Effect watching chat logs to snap scroll view down smoothly on new content updates
    effect(() => {
      const messages = this.complainDetail()?.chatHistoryList || [];
      if (messages.length > 0) {
        this.scrollToBottom();
      }
    });
  }

  ngOnInit(): void {
    const routeParams = this.route.snapshot.paramMap;
    const complainIdParam = Number(routeParams.get('complainId'));

    if (complainIdParam) {
      this.currentComplainId.set(complainIdParam);
    }

    this.isClosingTicketBtnAccess.set(this.checkAccessRoles.check([UserLoginRole.DEALER]));
  }

  getOriginalMessageBody(previousMessageId: number | null): string {
    if (!previousMessageId || !this.complainDetail()) return 'Original message not found';

    const history = this.complainDetail().chatHistoryList || [];
    const originalMsg = history.find((m: any) => m.id === previousMessageId);

    return originalMsg ? originalMsg.body : 'Original message missing';
  }

  setReplyTarget(msg: any): void {
    this.replyingToMessage.set(msg);
  }

  cancelReply(): void {
    this.replyingToMessage.set(null);
  }

  onSendMessage(): void {
    const messageText = this.chatInput().trim();
    if (!messageText || !this.complainDetail()) return;

    const history = this.complainDetail()?.chatHistoryList || [];
    const activeReply = this.replyingToMessage();
    const msgType = activeReply ? 'REPLY' : 'PRIMARY';
    const messageId = activeReply ? activeReply.id : (history.length > 0 ? history[history.length - 1].id : 1);

    this.complainService.createChatMessage(
      this.currentComplainId(),
      messageText,
      msgType,
      messageId
    ).pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.chatInput.set('');
        this.cancelReply();
        // Modern approach: Silently triggers an instant database sync refresh
        this.complainResource.reload();
      },
      error: (err) => console.error('Error processing messages', err)
    });
  }

  onCloseTicket(): void {
    if (!confirm('Are you sure you want to officially close this complaint ticket?')) return;

    this.isClosingTicketLoading.set(true);

    this.complainService.updateComplainStatus(this.currentComplainId(), 'CLOSE').pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: () => {
        this.isClosingTicketLoading.set(false);
        // Refresh the resource manager to synchronize clean state fields to template variables
        this.complainResource.reload();
      },
      error: (err) => {
        console.error('Failed to close ticket', err);
        this.isClosingTicketLoading.set(false);
      }
    });
  }

  navigateToList() {
    this.router.navigate(['/nav/complain'], { skipLocationChange: true });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.chatScrollContainer) {
        const element = this.chatScrollContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    }, 50);
  }
}
